import { Capacitor } from '@capacitor/core';
import { useEffect, useRef } from 'react';
import { useKeyboardStore } from '../stores/keyboard';
import { useScrollStore } from '../stores/scroll';

export const KeyboardAvoidingView = ({
  children,
  enabled = true,
}: {
  children: React.ReactNode;
  enabled?: boolean;
}) => {
  const { keyboardHeight, isOpen } = useKeyboardStore();
  const setIsContentScrolled = useScrollStore((s) => s.setIsContentScrolled);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const isNative = Capacitor.isNativePlatform();

  useEffect(() => {
    if (!enabled) {
      setIsContentScrolled(false);
      return;
    }
    const handleScroll = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target && typeof target.scrollTop === 'number') {
        const scrolled = target.scrollTop > 0;
        setIsContentScrolled(scrolled);
      }
    };
    document.addEventListener('scroll', handleScroll, {
      passive: true,
      capture: true,
    });
    return () =>
      document.removeEventListener('scroll', handleScroll, { capture: true });
  }, [enabled, setIsContentScrolled]);

  useEffect(() => {
    if (!enabled || !isOpen) {
      return;
    }

    // After a delay to let the keyboard appear, scroll focused input into view
    const timer = setTimeout(() => {
      const activeElement = document.activeElement;
      if (
        activeElement &&
        containerRef.current &&
        (activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'TEXTAREA')
      ) {
        // Check if element is behind keyboard
        const elementRect = activeElement.getBoundingClientRect();
        const viewportHeight = window.innerHeight;

        // With resize: 'none', the viewport stays full size
        // So we ALWAYS subtract keyboardHeight to get the available visible area
        const availableHeight = viewportHeight - keyboardHeight;

        const padding = 20;

        // If the element's bottom edge is below the top of the keyboard (plus padding)
        if (elementRect.bottom > availableHeight - padding) {
          // Calculate the exact scroll needed
          const scrollNeeded = elementRect.bottom - (availableHeight - padding);
          const currentScroll = containerRef.current.scrollTop;

          // Scroll just enough to bring element into view
          containerRef.current.scrollTo({
            top: currentScroll + scrollNeeded,
            behavior: 'smooth',
          });
        }
      }
    }, 150);

    return () => clearTimeout(timer);
  }, [enabled, isOpen, keyboardHeight]);

  if (!enabled) {
    return <>{children}</>;
  }

  // Calculate spacer height
  // Now that we use resize: 'none' on both platforms,
  // we ALWAYS add a spacer equal to keyboardHeight when native
  const spacerHeight = isNative ? keyboardHeight : 0;

  return (
    <div ref={containerRef} className="h-full w-full overflow-auto bg-white">
      <div ref={contentRef} className="h-full w-full">
        {children}
      </div>
      {/* Spacer that appears when keyboard is shown */}
      {spacerHeight > 0 && (
        <div
          style={{
            height: `${spacerHeight}px`,
            transition: 'height 0.2s ease-out',
          }}
        />
      )}
    </div>
  );
};
